type Envelope<T extends string, P extends Record<string, unknown>> = {
  type: T;
  ref?: number; // Client-generated int, echoed by server. Monotonically increasing recommended.
  payload: P;
};

export type PresenceUser = {
  user_id: number;
  connection_id: string;
  // Opaque presence metadata, always present. `null` when the connection has
  // set none / cleared it (see SPEC §7.5).
  data: unknown;
};

export type Connect = Envelope<'connect', { jwt: string }>;

export type Connected = Envelope<'connected', { user_id: number; connection_id: string }>;

export type Ping = Envelope<'ping', { timestamp: number }>;
export type Pong = Envelope<'pong', { timestamp: number }>;

type Subscribe = Envelope<
  'subscribe',
  {
    channel: string;
    jwt: string; // short lived JWT. has to match channel
  }
>;
export type Subscribed = Envelope<'subscribed', { channel: string; presence: PresenceUser[] }>;

export type Unsubscribe = Envelope<'unsubscribe', { channel: string }>;
export type Unsubscribed = Envelope<'unsubscribed', { channel: string }>;

export type SetPresence = Envelope<
  'set_presence',
  {
    channel: string;
    // Opaque metadata blob, full-replace. Serialized size must not exceed
    // `max_presence_bytes` (default 8 KiB). `null` clears presence.
    data: unknown;
  }
>;

export type Join = Envelope<'join', { channel: string; user: PresenceUser }>;
export type Leave = Envelope<'leave', { channel: string; user: PresenceUser }>;
export type PresenceUpdate = Envelope<'presence_update', { channel: string; user: PresenceUser }>;

export type BaseEvent = {
  event: string;
  data: any;
};

export type ChannelEvent<E extends BaseEvent = BaseEvent> = Envelope<
  'message',
  {
    channel: string;
    event: E['event'];
    data: E['data'];
    revision?: number;
    origin_connection_id?: string;
  }
>;

export type ErrorCode =
  | 'TOKEN_INVALID'
  | 'TOKEN_EXPIRED'
  | 'ISSUER_MISMATCH'
  | 'NOT_AUTHENTICATED'
  | 'NOT_SUBSCRIBED'
  | 'PAYLOAD_TOO_LARGE'
  | 'MALFORMED';

export type ConnectionError = Envelope<'error', { code: ErrorCode; message: string }>;
export type ChannelError = Envelope<'error', { code: ErrorCode; message: string; channel: string }>;

export type InboundChannelMessage =
  | Subscribed
  | Unsubscribed
  | ChannelEvent
  | ChannelError
  | Join
  | Leave
  | PresenceUpdate;
export type InboundConnectionMessage = Connected | Ping | ConnectionError;

export function isInboundChannelMessage(
  message: InboundChannelMessage | InboundConnectionMessage,
): message is InboundChannelMessage {
  return (message as InboundChannelMessage).payload.channel !== undefined;
}

export type InboundMessage = InboundChannelMessage | InboundConnectionMessage;

export type OutboundConnectionMessage = Connect | Pong;
export type OutboundChannelMessage = Subscribe | Unsubscribe | SetPresence;
export type OutboundMessage = OutboundConnectionMessage | OutboundChannelMessage;
